import { useState } from 'react';
import { format, subDays, startOfDay } from 'date-fns';
import { useData } from '@/contexts/DataContext';

/* ─── Types ─────────────────────────────────────────────── */
type ActivityTab = 'feed' | 'sleep' | 'diaper';

const TABS: { id: ActivityTab; label: string; emoji: string }[] = [
  { id: 'feed',   label: 'Feeds',   emoji: '🍼' },
  { id: 'sleep',  label: 'Sleep',   emoji: '😴' },
  { id: 'diaper', label: 'Diapers', emoji: '🧷' },
];

const ROWS = [
  { label: '12 AM', startH: 0,  endH: 6  },
  { label: '6 AM',  startH: 6,  endH: 12 },
  { label: '12 PM', startH: 12, endH: 18 },
  { label: '6 PM',  startH: 18, endH: 24 },
];

/* ─── Ghost patterns (shown when no real data yet) ───────── */
const FEED_GHOST: { bottle: boolean; breast: boolean }[][] = [
  [{ bottle: false, breast: false }, { bottle: false, breast: false }, { bottle: false, breast: false }, { bottle: false, breast: false }],
  [{ bottle: true,  breast: false }, { bottle: true,  breast: true  }, { bottle: true,  breast: false }, { bottle: false, breast: true  }],
  [{ bottle: true,  breast: false }, { bottle: true,  breast: false }, { bottle: false, breast: true  }, { bottle: true,  breast: false }],
  [{ bottle: false, breast: true  }, { bottle: true,  breast: false }, { bottle: true,  breast: true  }, { bottle: false, breast: true  }],
  [{ bottle: true,  breast: false }, { bottle: true,  breast: false }, { bottle: false, breast: true  }, { bottle: true,  breast: false }],
];

const SLEEP_GHOST: { fillRatio: number; isNight: boolean }[][] = [
  [{ fillRatio: 0.0,  isNight: false }, { fillRatio: 0.0,  isNight: false }, { fillRatio: 0.0,  isNight: false }, { fillRatio: 0.0,  isNight: false }],
  [{ fillRatio: 0.88, isNight: true  }, { fillRatio: 0.0,  isNight: false }, { fillRatio: 0.45, isNight: false }, { fillRatio: 0.25, isNight: false }],
  [{ fillRatio: 0.8,  isNight: true  }, { fillRatio: 0.0,  isNight: false }, { fillRatio: 0.35, isNight: false }, { fillRatio: 0.0,  isNight: false }],
  [{ fillRatio: 0.9,  isNight: true  }, { fillRatio: 0.0,  isNight: false }, { fillRatio: 0.5,  isNight: false }, { fillRatio: 0.3,  isNight: false }],
  [{ fillRatio: 0.85, isNight: true  }, { fillRatio: 0.0,  isNight: false }, { fillRatio: 0.4,  isNight: false }, { fillRatio: 0.0,  isNight: false }],
];

const DIAPER_GHOST: { wet: boolean; soiled: boolean }[][] = [
  [{ wet: false, soiled: false }, { wet: false, soiled: false }, { wet: false, soiled: false }, { wet: false, soiled: false }],
  [{ wet: false, soiled: false }, { wet: true,  soiled: false }, { wet: true,  soiled: true  }, { wet: true,  soiled: false }],
  [{ wet: false, soiled: false }, { wet: true,  soiled: true  }, { wet: true,  soiled: false }, { wet: true,  soiled: false }],
  [{ wet: false, soiled: false }, { wet: true,  soiled: false }, { wet: false, soiled: true  }, { wet: true,  soiled: false }],
  [{ wet: false, soiled: false }, { wet: true,  soiled: true  }, { wet: true,  soiled: false }, { wet: true,  soiled: true  }],
];

/* ─── Component ─────────────────────────────────────────── */
const ActivityChartWidget = () => {
  const { data } = useData();
  const [activeTab, setActiveTab] = useState<ActivityTab>('feed');
  const today = new Date();
  const days  = Array.from({ length: 5 }, (_, i) => subDays(today, i));

  /* ── Feed helpers ── */
  const getFeedCellContent = (day: Date, startH: number, endH: number) => {
    const dayStart = startOfDay(day);
    const wStart   = new Date(dayStart.getTime() + startH * 3_600_000);
    const wEnd     = new Date(dayStart.getTime() + endH   * 3_600_000);
    const feeds    = data.feeds.filter(f => { const ts = new Date(f.timestamp); return ts >= wStart && ts < wEnd; });
    return { hasBottle: feeds.some(f => f.type === 'bottle'), hasBreast: feeds.some(f => f.type === 'breastfeed'), isEmpty: feeds.length === 0 };
  };

  const getFeedDayStats = (day: Date) => {
    const dayStart = startOfDay(day);
    const dayEnd   = new Date(dayStart.getTime() + 86_400_000);
    const feeds    = data.feeds.filter(f => { const ts = new Date(f.timestamp); return ts >= dayStart && ts < dayEnd; });
    return {
      bottleMl:   feeds.filter(f => f.type === 'bottle').reduce((s, f) => s + (f.amount ?? 0), 0),
      breastMins: feeds.filter(f => f.type === 'breastfeed').reduce((s, f) => s + (f.duration ?? 0), 0),
    };
  };

  /* ── Sleep helpers ── */
  const getSleepInWindow = (day: Date, startH: number, endH: number) => {
    const dayStart    = startOfDay(day);
    const windowStart = new Date(dayStart.getTime() + startH * 3_600_000);
    const windowEnd   = new Date(dayStart.getTime() + endH   * 3_600_000);
    let totalMins = 0, hasNight = false, hasNap = false;
    data.sleeps.forEach(s => {
      const start        = new Date(s.startTime);
      const end          = new Date(s.endTime);
      const overlapStart = Math.max(start.getTime(), windowStart.getTime());
      const overlapEnd   = Math.min(end.getTime(),   windowEnd.getTime());
      if (overlapEnd > overlapStart) {
        totalMins += (overlapEnd - overlapStart) / 60_000;
        if (start.getHours() >= 6 && start.getHours() < 20) hasNap = true; else hasNight = true;
      }
    });
    return { fillRatio: Math.min(1, totalMins / 360), hasNight, hasNap, isEmpty: totalMins === 0 };
  };

  const getDayTotalMins = (day: Date) => {
    const dayStart = startOfDay(day);
    const dayEnd   = new Date(dayStart.getTime() + 86_400_000);
    let total = 0;
    data.sleeps.forEach(s => {
      const overlapStart = Math.max(new Date(s.startTime).getTime(), dayStart.getTime());
      const overlapEnd   = Math.min(new Date(s.endTime).getTime(),   dayEnd.getTime());
      if (overlapEnd > overlapStart) total += (overlapEnd - overlapStart) / 60_000;
    });
    return total;
  };

  /* ── Diaper helpers ── */
  const getDiaperCellContent = (day: Date, startH: number, endH: number) => {
    const dayStart    = startOfDay(day);
    const windowStart = new Date(dayStart.getTime() + startH * 3_600_000);
    const windowEnd   = new Date(dayStart.getTime() + endH   * 3_600_000);
    const diapers     = data.diapers.filter(d => { const ts = new Date(d.timestamp); return ts >= windowStart && ts < windowEnd; });
    return { hasWet: diapers.some(d => d.isPee), hasSoiled: diapers.some(d => d.isPoop), isEmpty: diapers.length === 0 };
  };

  const getDayDiaperCount = (day: Date) => {
    const dayStart = startOfDay(day);
    const dayEnd   = new Date(dayStart.getTime() + 86_400_000);
    return data.diapers.filter(d => { const ts = new Date(d.timestamp); return ts >= dayStart && ts < dayEnd; }).length;
  };

  /* ── Background bar values per tab ── */
  const feedStats   = days.map(getFeedDayStats);
  const feedScores  = feedStats.map(s => s.bottleMl + s.breastMins * 4);
  const sleepMins   = days.map(getDayTotalMins);
  const diaperCounts = days.map(getDayDiaperCount);

  const bgData: { values: number[]; color: string } = (() => {
    if (activeTab === 'feed') {
      const max = Math.max(1, ...feedScores);
      return { values: feedScores.map(s => (s / max) * 100), color: 'hsl(var(--feed-accent))' };
    }
    if (activeTab === 'sleep') {
      const max = Math.max(1, ...sleepMins);
      return { values: sleepMins.map(m => (m / max) * 100), color: 'hsl(var(--sleep-accent))' };
    }
    const max = Math.max(1, ...diaperCounts);
    return { values: diaperCounts.map(c => (c / max) * 100), color: '#0ea5e9' };
  })();

  return (
    <div className="bg-card rounded-3xl p-5 shadow-sm">
      {/* Header */}
      <h3 className="text-base font-semibold text-foreground mb-3">📅 Activity Chart</h3>

      {/* Tab selector */}
      <div className="flex gap-1 mb-4 p-1 rounded-xl" style={{ background: 'hsl(var(--muted))' }}>
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className="flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all"
            style={activeTab === tab.id
              ? { background: 'white', color: 'hsl(var(--foreground))', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }
              : { background: 'transparent', color: 'hsl(var(--muted-foreground))' }
            }
          >
            {tab.emoji} {tab.label}
          </button>
        ))}
      </div>

      {/* Day headers */}
      <div className="flex">
        <div className="w-12 flex-shrink-0" />
        {days.map((day, i) => (
          <div key={i} className="flex-1 text-center">
            <p className={`text-[10px] font-semibold ${i === 0 ? 'text-primary' : 'text-muted-foreground'}`}>
              {format(day, 'EEE').toUpperCase()}
            </p>
            <p className={`text-sm font-bold ${i === 0 ? 'text-primary' : 'text-foreground'}`}>
              {format(day, 'd')}
            </p>
          </div>
        ))}
      </div>

      {/* Grid */}
      <div className="relative mt-2">
        {/* Background bars */}
        <div className="absolute inset-0 flex pointer-events-none" aria-hidden="true">
          <div className="w-12 flex-shrink-0" />
          {bgData.values.map((pct, i) => (
            <div key={i} className="flex-1 flex items-end justify-center px-1">
              <div
                className="w-full rounded-t-md transition-all duration-500"
                style={{ height: `${pct}%`, background: bgData.color, opacity: 0.12 }}
              />
            </div>
          ))}
        </div>

        {/* Rows */}
        {ROWS.map((row, rowIdx) => (
          <div key={row.label} className="flex items-center h-9">
            <div className="w-12 flex-shrink-0">
              <span className="text-[10px] text-muted-foreground">{row.label}</span>
            </div>

            {days.map((day, dayIdx) => {
              /* ── Feed cell ── */
              if (activeTab === 'feed') {
                const { hasBottle, hasBreast, isEmpty } = getFeedCellContent(day, row.startH, row.endH);
                const ghost     = FEED_GHOST[dayIdx][rowIdx];
                const showGhost = isEmpty && (ghost.bottle || ghost.breast);
                return (
                  <div key={dayIdx} className="flex-1 flex items-center justify-center gap-0.5">
                    {!isEmpty ? (
                      <>
                        {hasBottle && <span className="text-base leading-none">🍼</span>}
                        {hasBreast && <span className="text-base leading-none">🤱</span>}
                      </>
                    ) : showGhost ? (
                      <span className="text-base leading-none opacity-20">
                        {ghost.bottle ? '🍼' : '🤱'}
                      </span>
                    ) : (
                      <div className="w-1.5 h-1.5 rounded-full bg-border/30" />
                    )}
                  </div>
                );
              }

              /* ── Sleep cell ── */
              if (activeTab === 'sleep') {
                const { fillRatio, hasNight, hasNap, isEmpty } = getSleepInWindow(day, row.startH, row.endH);
                const isNight   = hasNight && !hasNap;
                const ghost     = SLEEP_GHOST[dayIdx][rowIdx];
                const showGhost = isEmpty && ghost.fillRatio > 0;
                return (
                  <div key={dayIdx} className="flex-1 flex items-center justify-center h-full px-0.5">
                    {!isEmpty ? (
                      <div
                        className="w-full rounded-lg flex items-center justify-center"
                        style={{
                          height: `${Math.max(30, Math.round(fillRatio * 100))}%`,
                          background: isNight ? 'hsl(var(--sleep-accent) / 0.4)' : 'hsl(var(--sleep-accent) / 0.25)',
                          border: `1px solid ${isNight ? 'hsl(var(--sleep-accent) / 0.3)' : 'hsl(var(--sleep-accent) / 0.2)'}`,
                        }}
                      >
                        <span className="text-xs leading-none">😴</span>
                      </div>
                    ) : showGhost ? (
                      <div
                        className="w-full rounded-lg flex items-center justify-center opacity-20"
                        style={{
                          height: `${Math.max(30, Math.round(ghost.fillRatio * 100))}%`,
                          background: ghost.isNight ? 'hsl(var(--sleep-accent) / 0.4)' : 'hsl(var(--sleep-accent) / 0.25)',
                          border: `1px solid ${ghost.isNight ? 'hsl(var(--sleep-accent) / 0.3)' : 'hsl(var(--sleep-accent) / 0.2)'}`,
                        }}
                      >
                        <span className="text-xs leading-none">😴</span>
                      </div>
                    ) : (
                      <div className="w-1.5 h-1.5 rounded-full bg-border/30" />
                    )}
                  </div>
                );
              }

              /* ── Diaper cell ── */
              const { hasWet, hasSoiled, isEmpty } = getDiaperCellContent(day, row.startH, row.endH);
              const realEmoji  = hasWet && hasSoiled ? '💧💩' : hasSoiled ? '💩' : hasWet ? '💧' : null;
              const ghost      = DIAPER_GHOST[dayIdx][rowIdx];
              const ghostEmoji = ghost.wet && ghost.soiled ? '💧💩' : ghost.soiled ? '💩' : ghost.wet ? '💧' : null;
              const showGhost  = isEmpty && ghostEmoji !== null;
              return (
                <div key={dayIdx} className="flex-1 flex items-center justify-center h-full">
                  {!isEmpty ? (
                    <span className="text-base leading-none">{realEmoji}</span>
                  ) : showGhost ? (
                    <span className="text-base leading-none opacity-20">{ghostEmoji}</span>
                  ) : (
                    <div className="w-1.5 h-1.5 rounded-full bg-border/30" />
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Totals row */}
      <div className="flex mt-1 pt-2 border-t border-border/20">
        <div className="w-12 flex-shrink-0" />
        {days.map((_, i) => {
          let label = '–';
          let color = 'hsl(var(--muted-foreground))';

          if (activeTab === 'feed') {
            const s = feedStats[i];
            if (s.bottleMl > 0 || s.breastMins > 0) {
              label = s.bottleMl > 0 ? `${s.bottleMl}ml` : `${s.breastMins}m`;
              color = 'hsl(var(--feed-accent))';
            }
          } else if (activeTab === 'sleep') {
            const mins = sleepMins[i];
            if (mins > 0) {
              const h = Math.floor(mins / 60), m = Math.round(mins % 60);
              label = m > 0 ? `${h}h ${m}m` : `${h}h`;
              color = 'hsl(var(--sleep-accent))';
            }
          } else {
            const c = diaperCounts[i];
            if (c > 0) { label = `${c}x`; color = '#0ea5e9'; }
          }

          return (
            <div key={i} className="flex-1 text-center">
              <span className="text-[10px] font-semibold" style={{ color }}>{label}</span>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mt-3 pt-3 border-t border-border/30">
        {activeTab === 'feed' && (
          <>
            <div className="flex items-center gap-1.5">
              <span className="text-sm">🍼</span>
              <span className="text-xs text-muted-foreground">Bottle</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-sm">🤱</span>
              <span className="text-xs text-muted-foreground">Breast</span>
            </div>
          </>
        )}
        {activeTab === 'sleep' && (
          <>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-sm" style={{ background: 'hsl(var(--sleep-accent) / 0.25)', border: '1px solid hsl(var(--sleep-accent) / 0.2)' }} />
              <span className="text-xs text-muted-foreground">Nap</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-sm" style={{ background: 'hsl(var(--sleep-accent) / 0.4)', border: '1px solid hsl(var(--sleep-accent) / 0.3)' }} />
              <span className="text-xs text-muted-foreground">Night</span>
            </div>
          </>
        )}
        {activeTab === 'diaper' && (
          <>
            <div className="flex items-center gap-1.5">
              <span className="text-sm">💧</span>
              <span className="text-xs text-muted-foreground">Wet</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-sm">💩</span>
              <span className="text-xs text-muted-foreground">Soiled</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-sm">💧💩</span>
              <span className="text-xs text-muted-foreground">Both</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ActivityChartWidget;
